import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, Car, Trophy, Clock, ArrowUp, ArrowDown, Plus, Zap,
  PhoneCall, FileText, ChevronRight, AlertTriangle, Activity as ActivityIcon,
  Flame, CircleDot,
} from 'lucide-react';
import { useDealershipData } from '@/data/hooks/useDealershipData';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import type { Activity, ActivityType, DealStage } from '@/types';
import { STAGE_LABELS, STAGE_ORDER } from '@/types';
import { WishlistManagerSection } from '@/components/wishlist/WishlistManagerSection';
import { CustomerDemandCard } from '@/components/wishlist/CustomerDemandCard';

/* ---------- helpers ---------- */
function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const POSITIVE: ActivityType[] = ['test_drive', 'walkaround', 'quote_review', 'appointment_set'];
const NEUTRAL: ActivityType[] = ['phone_call', 'followup_video', 'text_sent', 'email_sent', 'note_added'];

function activityTone(t: ActivityType): 'positive' | 'neutral' | 'attention' {
  if (POSITIVE.includes(t)) return 'positive';
  if (NEUTRAL.includes(t)) return 'neutral';
  return 'attention';
}

const ACTIVITY_LABEL: Record<ActivityType, string> = {
  walkaround: 'completed a walkaround',
  test_drive: 'booked a test drive',
  quote_review: 'reviewed numbers',
  followup_video: 'sent a follow-up video',
  phone_call: 'logged a phone call',
  appointment_set: 'set an appointment',
  text_sent: 'sent a text',
  email_sent: 'sent an email',
  note_added: 'added a note',
};

/* ---------- simulated ticker ---------- */
const SIM_NAMES = ['Olivia', 'Marcus', 'Priya', 'Diego', 'Hannah', 'Wei', 'Chloe', 'Andre'];
const SIM_REPS = ['Alex Carter', 'Maria Lopez', 'Ryan Mitchell', 'Samantha Reed'];
const SIM_TYPES: ActivityType[] = ['walkaround', 'test_drive', 'phone_call', 'followup_video', 'appointment_set', 'quote_review'];

function makeSimActivity(): Activity {
  const t = SIM_TYPES[Math.floor(Math.random() * SIM_TYPES.length)];
  const name = SIM_NAMES[Math.floor(Math.random() * SIM_NAMES.length)];
  const rep = SIM_REPS[Math.floor(Math.random() * SIM_REPS.length)];
  return {
    id: `sim-${Date.now()}-${Math.random()}`,
    dealership_id: 'sim',
    deal_id: 'sim',
    rep_id: 'sim',
    rep_name: rep,
    activity_type: t,
    details: '',
    customer_name: name,
    created_at: new Date().toISOString(),
  };
}

/* ---------- subcomponents ---------- */
function KpiTile({ label, value, icon: Icon, trend }: { label: string; value: string | number; icon: any; trend?: { dir: 'up' | 'down'; text: string } }) {
  return (
    <Card className="relative overflow-hidden border-border/60 bg-card/80 p-5 transition-all hover:border-primary/40 hover:shadow-[0_0_30px_-10px_hsl(var(--primary)/0.5)]">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className="mt-2 text-4xl font-bold tracking-tight text-foreground">{value}</p>
          {trend && (
            <div className={`mt-2 inline-flex items-center gap-1 text-xs font-medium ${trend.dir === 'up' ? 'text-green-400' : 'text-red-400'}`}>
              {trend.dir === 'up' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
              {trend.text}
            </div>
          )}
        </div>
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <div className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-primary/5 blur-2xl" />
    </Card>
  );
}

function ActivityRow({ a }: { a: Activity }) {
  const tone = activityTone(a.activity_type);
  const dot = tone === 'positive' ? 'bg-green-400' : tone === 'neutral' ? 'bg-yellow-400' : 'bg-red-400';
  const ring = tone === 'positive' ? 'ring-green-400/20' : tone === 'neutral' ? 'ring-yellow-400/20' : 'ring-red-400/20';
  return (
    <div className="group flex items-start gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-muted/30">
      <div className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ring-4 ${ring} ${dot}`} />
      <div className="min-w-0 flex-1">
        <p className="text-sm leading-snug text-foreground">
          <span className="font-semibold">{a.rep_name}</span>{' '}
          <span className="text-muted-foreground">{ACTIVITY_LABEL[a.activity_type] ?? a.activity_type}</span>
          {a.customer_name && <> with <span className="font-medium">{a.customer_name}</span></>}
        </p>
        {a.details && <p className="mt-0.5 truncate text-xs text-muted-foreground">{a.details}</p>}
      </div>
      <span className="shrink-0 text-xs text-muted-foreground">{timeAgo(a.created_at)}</span>
    </div>
  );
}

/* ---------- main page ---------- */
export default function ManagerDashboardV2() {
  const { dealershipName } = useAuth();
  const { deals, activities, floorReps } = useDealershipData();
  const [simActivities, setSimActivities] = useState<Activity[]>([]);

  // Simulated ticker — adds an event every 12-25s
  useEffect(() => {
    let t: number;
    const tick = () => {
      setSimActivities(prev => [makeSimActivity(), ...prev].slice(0, 8));
      t = window.setTimeout(tick, 12000 + Math.random() * 13000);
    };
    t = window.setTimeout(tick, 4000);
    return () => window.clearTimeout(t);
  }, []);

  /* KPIs */
  const kpis = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const isToday = (iso: string) => new Date(iso) >= today;
    const leadsToday = deals.filter(d => isToday(d.created_at)).length;
    const demosToday = activities.filter(a =>
      isToday(a.created_at) && ['walkaround', 'test_drive', 'quote_review'].includes(a.activity_type),
    ).length;
    const dealsClosed = deals.filter(d => d.stage === 'sold').length;
    const followUpsPending = deals.filter(d => d.next_step === 'follow_up' || d.next_step === 'call').length;
    return { leadsToday, demosToday, dealsClosed, followUpsPending };
  }, [deals, activities]);

  /* Pipeline counts */
  const pipeline = useMemo(() => {
    const counts = STAGE_ORDER.reduce<Record<DealStage, number>>((acc, s) => { acc[s] = 0; return acc; }, {} as any);
    deals.forEach(d => { counts[d.stage] = (counts[d.stage] ?? 0) + 1; });
    return counts;
  }, [deals]);

  /* Live feed = real + simulated, newest first */
  const liveFeed = useMemo(() => {
    return [...simActivities, ...activities]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 14);
  }, [simActivities, activities]);

  /* Team perf: derive close rate per rep */
  const teamPerf = useMemo(() => {
    const map = new Map<string, { name: string; leads: number; demos: number; sold: number }>();
    deals.forEach(d => {
      const e = map.get(d.rep_id) ?? { name: d.rep_name, leads: 0, demos: 0, sold: 0 };
      e.leads += 1;
      if (d.stage === 'sold') e.sold += 1;
      map.set(d.rep_id, e);
    });
    activities.forEach(a => {
      if (['walkaround', 'test_drive', 'quote_review'].includes(a.activity_type)) {
        const e = map.get(a.rep_id);
        if (e) e.demos += 1;
      }
    });
    const rows = Array.from(map.values()).map(r => ({
      ...r,
      closeRate: r.leads ? Math.round((r.sold / r.leads) * 100) : 0,
    }));
    rows.sort((a, b) => b.closeRate - a.closeRate);
    return rows;
  }, [deals, activities]);

  /* Alerts */
  const alerts = useMemo(() => {
    const dayAgo = Date.now() - 24 * 3600 * 1000;
    const notContacted = deals.filter(d => d.stage === 'new' && new Date(d.last_activity_at).getTime() < dayAgo);
    const stuck = deals.filter(d => ['contacted', 'demo_done', 'test_drive'].includes(d.stage) && d.status !== 'active');
    const noFollowUp = deals.filter(d => d.stage === 'demo_done' && !d.next_step);
    return { notContacted, stuck, noFollowUp };
  }, [deals]);

  return (
    <div className="space-y-6">
      {/* Tagline + Quick Actions */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary">Turning attention into action</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight">{dealershipName || 'Dealership'} — Live Floor</h1>
          <p className="text-sm text-muted-foreground">What's happening right now, and what needs to happen next.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild size="sm" className="gap-1.5"><Link to="/log-activity"><Plus className="h-4 w-4" />Add Lead</Link></Button>
          <Button asChild size="sm" variant="secondary" className="gap-1.5"><Link to="/log-activity"><Car className="h-4 w-4" />Log Demo</Link></Button>
          <Button asChild size="sm" variant="secondary" className="gap-1.5"><Link to="/log-activity"><PhoneCall className="h-4 w-4" />Log Follow-Up</Link></Button>
          <Button asChild size="sm" variant="outline" className="gap-1.5"><Link to="/deals"><FileText className="h-4 w-4" />Move Deal</Link></Button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiTile label="Leads Today" value={kpis.leadsToday} icon={Users} trend={{ dir: 'up', text: '+12% vs yesterday' }} />
        <KpiTile label="Demos Booked" value={kpis.demosToday} icon={Car} trend={{ dir: 'up', text: '+5%' }} />
        <KpiTile label="Deals Closed" value={kpis.dealsClosed} icon={Trophy} trend={{ dir: 'up', text: 'On pace' }} />
        <KpiTile label="Follow-Ups Pending" value={kpis.followUpsPending} icon={Clock} trend={{ dir: 'down', text: '-3 from yesterday' }} />
      </div>

      {/* Live Feed + Alerts */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="border-border/60 bg-card/80 lg:col-span-2">
          <div className="flex items-center justify-between border-b border-border/60 px-5 py-3.5">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary" />
              </span>
              <h2 className="text-sm font-semibold uppercase tracking-wider">Live Floor Activity</h2>
            </div>
            <span className="text-xs text-muted-foreground">{liveFeed.length} events</span>
          </div>
          <div className="max-h-[420px] overflow-y-auto p-2">
            {liveFeed.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-muted-foreground">
                <ActivityIcon className="h-8 w-8 opacity-40" />
                <p className="text-sm">Waiting for floor activity…</p>
              </div>
            ) : liveFeed.map(a => <ActivityRow key={a.id} a={a} />)}
          </div>
        </Card>

        {/* Alerts panel */}
        <Card className="border-border/60 bg-card/80">
          <div className="flex items-center gap-2 border-b border-border/60 px-5 py-3.5">
            <AlertTriangle className="h-4 w-4 text-yellow-400" />
            <h2 className="text-sm font-semibold uppercase tracking-wider">Manager Alerts</h2>
          </div>
          <div className="space-y-3 p-4">
            <AlertRow color="red" count={alerts.notContacted.length} label="Leads not contacted in 24h" />
            <AlertRow color="yellow" count={alerts.noFollowUp.length} label="Demos without follow-up" />
            <AlertRow color="yellow" count={alerts.stuck.length} label="Deals stuck in pipeline" />
          </div>
        </Card>
      </div>

      {/* Pipeline */}
      <Card className="border-border/60 bg-card/80 p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wider">Deal Pipeline</h2>
          <Link to="/deals" className="inline-flex items-center text-xs text-primary hover:underline">
            View all <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="grid grid-cols-3 gap-3 md:grid-cols-6">
          {STAGE_ORDER.map((stage, idx) => {
            const isLast = stage === 'sold';
            return (
              <Link
                key={stage}
                to="/deals"
                className={`group relative rounded-xl border p-4 transition-all hover:-translate-y-0.5 ${
                  isLast
                    ? 'border-primary/40 bg-gradient-to-br from-primary/15 to-primary/5'
                    : 'border-border/60 bg-muted/20 hover:border-primary/40'
                }`}
              >
                <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Stage {idx + 1}</p>
                <p className="mt-1 text-sm font-semibold text-foreground">{STAGE_LABELS[stage]}</p>
                <p className={`mt-3 text-3xl font-bold ${isLast ? 'text-primary' : 'text-foreground'}`}>{pipeline[stage]}</p>
              </Link>
            );
          })}
        </div>
      </Card>

      {/* Customer Wishlists */}
      <CustomerDemandCard />
      <WishlistManagerSection />

      {/* Team Performance */}
      <Card className="border-border/60 bg-card/80">
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3.5">
          <h2 className="text-sm font-semibold uppercase tracking-wider">Team Performance</h2>
          <Link to="/leaderboard" className="inline-flex items-center text-xs text-primary hover:underline">
            Full leaderboard <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-5 py-3 font-medium">Rep</th>
                <th className="px-3 py-3 font-medium">Leads</th>
                <th className="px-3 py-3 font-medium">Demos</th>
                <th className="px-3 py-3 font-medium">Sold</th>
                <th className="px-5 py-3 font-medium">Close Rate</th>
              </tr>
            </thead>
            <tbody>
              {teamPerf.map((r, i) => {
                const top = i === 0 && r.closeRate > 0;
                const under = i === teamPerf.length - 1 && teamPerf.length > 1 && r.closeRate < 20;
                return (
                  <tr key={r.name} className="border-t border-border/40 transition-colors hover:bg-muted/20">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        {top && <Flame className="h-4 w-4 text-green-400" />}
                        {under && <CircleDot className="h-4 w-4 text-red-400" />}
                        <span className={`font-medium ${top ? 'text-green-400' : under ? 'text-red-400' : 'text-foreground'}`}>{r.name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-muted-foreground">{r.leads}</td>
                    <td className="px-3 py-3 text-muted-foreground">{r.demos}</td>
                    <td className="px-3 py-3 text-muted-foreground">{r.sold}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-24 overflow-hidden rounded-full bg-muted">
                          <div
                            className={`h-full ${top ? 'bg-green-400' : under ? 'bg-red-400' : 'bg-primary'}`}
                            style={{ width: `${Math.min(100, r.closeRate)}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-foreground">{r.closeRate}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {teamPerf.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-sm text-muted-foreground">No rep data yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function AlertRow({ color, count, label }: { color: 'red' | 'yellow'; count: number; label: string }) {
  const tone = color === 'red' ? 'text-red-400 bg-red-400/10 ring-red-400/20' : 'text-yellow-400 bg-yellow-400/10 ring-yellow-400/20';
  return (
    <div className="flex items-center justify-between rounded-lg border border-border/40 bg-muted/20 px-3 py-2.5">
      <span className="text-sm text-foreground">{label}</span>
      <span className={`inline-flex h-7 min-w-7 items-center justify-center rounded-full px-2 text-xs font-bold ring-1 ${tone}`}>{count}</span>
    </div>
  );
}
